/**
 * UK VAT & Corporation Tax Summary Utility
 * Computes VAT and Corporation Tax based on UK HMRC rules (FY 2023+)
 * 
 * VAT: Extracted at 20% from VAT-inclusive turnover
 * Corporation Tax: 19% (small profits), 25% (main rate), marginal relief between £50k-£250k
 */

// UK Corporation Tax thresholds and rates (FY 2023+)
const CT_LOWER_LIMIT = 50000;
const CT_UPPER_LIMIT = 250000;
const CT_SMALL_RATE = 0.19;
const CT_MAIN_RATE = 0.25;
const CT_MARGINAL_FRACTION = 3 / 200; // HMRC standard marginal fraction

/**
 * Computes UK VAT and Corporation Tax breakdown
 * @param {Object} params - Input parameters
 * @param {number|null|undefined} params.totalSales - VAT-inclusive total sales/turnover
 * @param {number|null|undefined} params.expenses - Allowable business expenses
 * @returns {Object} - Complete tax breakdown
 */
export function computeUkBusinessTaxes({ totalSales, expenses }) {
  const result = {
    // VAT section
    totalSales: null,
    vatCollected: null,
    netTurnover: null,
    
    // Profit section
    expenses: null,
    netProfit: null,
    
    // Corporation Tax section
    corporationTax: null,
    postTaxProfit: null,
    takeHome: null,  // Alias for postTaxProfit (what the owner takes home after all taxes)
    effectiveCtRate: null,
    taxBand: null,
    
    // Metadata
    isValid: false,
    hasSalesData: false,
    hasExpensesData: false,
    hasLoss: false,
    messages: []
  };

  // Check if sales data is available
  if (totalSales === null || totalSales === undefined || isNaN(totalSales)) {
    result.messages.push('No sales data available');
    return result;
  }

  // Process sales (clamp to 0 if negative)
  const sales = Math.max(0, Number(totalSales));
  result.hasSalesData = true;
  
  // ==================== VAT SECTION ====================
  // VAT = totalSales * (20/120) - extracts VAT from VAT-inclusive amount
  const vat = sales * (20 / 120);
  const netTurnover = sales - vat;
  
  result.totalSales = roundTo2dp(sales);
  result.vatCollected = roundTo2dp(vat);
  result.netTurnover = roundTo2dp(netTurnover);
  
  // ==================== PROFIT SECTION ====================
  // Check if expenses data is available
  const hasExpenses = expenses !== null && expenses !== undefined && !isNaN(expenses);
  
  if (hasExpenses) {
    const exp = Math.max(0, Number(expenses));
    result.expenses = roundTo2dp(exp);
    result.hasExpensesData = true;
    
    // Net Profit = Net Turnover - Expenses
    const profit = netTurnover - exp;
    result.netProfit = roundTo2dp(profit);
    
    // ==================== CORPORATION TAX SECTION ====================
    if (profit <= 0) {
      // No tax on losses
      result.hasLoss = true;
      result.corporationTax = 0;
      result.postTaxProfit = roundTo2dp(profit);
      result.takeHome = roundTo2dp(profit);  // Same as postTaxProfit
      result.effectiveCtRate = 0;
      result.taxBand = 'loss';
      result.messages.push('No Corporation Tax due on losses.');
    } else {
      // Calculate Corporation Tax based on profit band
      const ctResult = calculateCorporationTax(profit);
      result.corporationTax = ctResult.tax;
      result.postTaxProfit = roundTo2dp(profit - ctResult.tax);
      result.takeHome = roundTo2dp(profit - ctResult.tax);  // Same as postTaxProfit
      result.effectiveCtRate = roundTo2dp((ctResult.tax / profit) * 100);
      result.taxBand = ctResult.band;
      
      if (ctResult.band === 'marginal') {
        result.messages.push(`Marginal relief applied (profit between £50k-£250k)`);
      }
    }
  } else {
    result.messages.push('Expenses data not available - showing VAT calculations only');
  }
  
  result.isValid = true;
  return result;
}

/**
 * Calculate Corporation Tax with proper UK rates and marginal relief
 * @param {number} profit - Net profit before tax
 * @returns {Object} - Tax amount and band
 */
function calculateCorporationTax(profit) {
  if (profit <= 0) {
    return { tax: 0, band: 'loss' };
  }
  
  if (profit <= CT_LOWER_LIMIT) {
    // Small profits rate: 19%
    return {
      tax: roundTo2dp(profit * CT_SMALL_RATE),
      band: 'small'
    };
  }
  
  if (profit >= CT_UPPER_LIMIT) {
    // Main rate: 25%
    return {
      tax: roundTo2dp(profit * CT_MAIN_RATE),
      band: 'main'
    };
  }
  
  // Marginal relief band (£50,000 - £250,000)
  // HMRC Formula: Tax = (profit × 25%) - ((upper - profit) × 3/200)
  const mainTax = profit * CT_MAIN_RATE;
  const marginalRelief = (CT_UPPER_LIMIT - profit) * CT_MARGINAL_FRACTION;
  const tax = mainTax - marginalRelief;
  
  return {
    tax: roundTo2dp(tax),
    band: 'marginal'
  };
}

/**
 * Rounds a number to 2 decimal places
 * @param {number} value 
 * @returns {number}
 */
function roundTo2dp(value) {
  return Math.round(value * 100) / 100;
}

/**
 * Formats a number as GBP currency
 * @param {number|null} value 
 * @returns {string}
 */
export function formatGBP(value) {
  if (value === null || value === undefined) {
    return '—';
  }
  return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Generates a text summary for clipboard copy
 * @param {Object} breakdown - The computed breakdown
 * @returns {string}
 */
export function generateBreakdownText(breakdown) {
  if (!breakdown.isValid) {
    return 'No data available';
  }

  let text = `UK VAT & Corporation Tax Summary
══════════════════════════════════════════

VAT SECTION
───────────
Total Sales (VAT inclusive):    ${formatGBP(breakdown.totalSales)}
VAT collected (20% extracted):  ${formatGBP(breakdown.vatCollected)}
Net Turnover (ex VAT):          ${formatGBP(breakdown.netTurnover)}
`;

  if (breakdown.hasExpensesData) {
    text += `
PROFIT SECTION
──────────────
Allowable Expenses:             ${formatGBP(breakdown.expenses)}
Net Profit (before tax):        ${formatGBP(breakdown.netProfit)}

CORPORATION TAX
───────────────
`;
    if (breakdown.hasLoss) {
      text += `Corporation Tax due:            £0.00
Post-tax profit:                ${formatGBP(breakdown.postTaxProfit)}
Note: No Corporation Tax due on losses.
`;
    } else {
      text += `Tax Band:                       ${breakdown.taxBand === 'small' ? 'Small profits (19%)' : breakdown.taxBand === 'main' ? 'Main rate (25%)' : 'Marginal relief (19-25%)'}
Corporation Tax due:            ${formatGBP(breakdown.corporationTax)}
Post-tax profit:                ${formatGBP(breakdown.postTaxProfit)}
Effective CT rate:              ${breakdown.effectiveCtRate}%
`;
    }
  } else {
    text += `
Note: Expenses data not available.
`;
  }

  text += `
══════════════════════════════════════════
Estimates based on UK Corporation Tax rates (19%–25%)
and standard VAT at 20%. Not financial advice.`;

  return text;
}

/**
 * Tooltip definitions for UI
 */
export const TAX_TOOLTIPS = {
  turnover: "Total sales revenue including VAT. This is your gross income before any deductions.",
  vat: "Value Added Tax collected at 20%. This is output VAT before any input VAT reclaims on purchases.",
  netTurnover: "Sales revenue after VAT is extracted. This is your actual business income.",
  expenses: "Allowable business costs that can be deducted from turnover to calculate taxable profit.",
  profit: "Net income after deducting allowable expenses from net turnover. This is the amount subject to Corporation Tax.",
  corporationTax: "UK Corporation Tax on company profits. Rates: 19% for profits ≤£50k, 25% for profits ≥£250k, marginal relief in between.",
  marginalRelief: "Tax relief for profits between £50,000 and £250,000. The effective rate gradually increases from 19% to 25%.",
  effectiveRate: "The actual percentage of profit paid as Corporation Tax after any marginal relief is applied.",
  takeHome: "Your take-home profit after VAT and Corporation Tax. This is what you actually keep from your business revenue."
};
